/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { partnerRoles } from 'const';
import { CREATE_ACCOUNT } from 'redux/sagas/actions/people';

import { Button, DropDown } from 'ui/atoms';
import MainFont from 'ui/typography/mainFont';
import validator from 'utils/regex-validator';

import {
  AcceptButtonContainer,
  ButtonContainer,
  CreateContainer,
  InputContainer,
  ItemContainer,
  RoleIcon,
  TitleContainer
} from './style';
import { isPartnerAdmin } from '../../../utils/common';
import { SpinnerIcon } from '../../atoms/Table/style';
import spinnerIconWhite from '../../assets/img/spinner-icon-white.png';
import { RoleImages } from 'utils/roles';

const CreateAccount = ({
  createAccount,
  currentUserRole,
  createAccountCorporate,
  companies,
  currentCompanyCode,
  isLoadingCreation
}) => {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState(false);
  const [roleError, setRoleError] = useState(false);
  const [companySelected, setCompanySelected] = useState(null);

  useEffect(() => {
    if (currentUserRole === partnerRoles.COMMERCIAL_PARTNER_SUPER_USER) {
      setRole(partnerRoles.COMMERCIAL_PARTNER_USER);
    }
    if (currentUserRole === partnerRoles.TECHNICAL_PARTNER_SUPER_USER) {
      setRole(partnerRoles.TECHNICAL_PARTNER_USER);
    }
  }, [currentUserRole]);

  const canShowDropdown = () =>
    currentUserRole === partnerRoles.ADMIN ||
    currentUserRole === partnerRoles.SYSTEM_ADMIN;

  const canChooseRole = () =>
    currentUserRole !== partnerRoles.COMMERCIAL_PARTNER_SUPER_USER &&
    currentUserRole !== partnerRoles.TECHNICAL_PARTNER_SUPER_USER;

  const onCreateAccount = () => {
    if (!email && !role) {
      setError(true);
      setRoleError(true);
      return;
    }

    if (!email || error) {
      setError(true);
      return;
    }

    if (!role) {
      setRoleError(true);
      return;
    }

    if (!canShowDropdown() && email) {
      createAccount({
        email: email.trim(),
        role,
        companyCode: currentCompanyCode
      });
      return;
    }
    createAccount({ email: email.trim(), role, companyCode: companySelected });
  };

  const onInputChange = e => {
    const enteredValue = e.target.value;
    if (enteredValue && !validator.email(enteredValue.trim())) {
      setError(true);
    } else {
      setError(false);
    }
    setEmail(e.target.value);
  };

  const onRoleSelect = e => {
    setRole(e.target.value);
    setRoleError(false);
  };

  const isDisabled = () => {
    return (
      error || !email || (isPartnerAdmin(currentUserRole) && !companySelected)
    );
  };

  const loadCorrectRoles = () => (
    <>
      <Button
        variant="create-account-buttons"
        selected={role === partnerRoles.COMMERCIAL_PARTNER_SUPER_USER}
        value={partnerRoles.COMMERCIAL_PARTNER_SUPER_USER}
        onClick={onRoleSelect}
        error={roleError}
        disabled={currentUserRole !== partnerRoles.ADMIN}
      >
        <RoleIcon icon={RoleImages.BusinessAdmin} />
        {t('comm_partner_admin')}
      </Button>
      <Button
        variant="create-account-buttons"
        selected={role === partnerRoles.COMMERCIAL_PARTNER_USER}
        value={partnerRoles.COMMERCIAL_PARTNER_USER}
        onClick={onRoleSelect}
        error={roleError}
        disabled={
          currentUserRole !== partnerRoles.ADMIN &&
          currentUserRole !== partnerRoles.COMMERCIAL_PARTNER_SUPER_USER
        }
      >
        <RoleIcon icon={RoleImages.BusinessUser} />
        {t('comm_partner')}
      </Button>
     {/*  <Button
        variant="create-account-buttons"
        selected={role === partnerRoles.TECHNICAL_PARTNER_SUPER_USER}
        value={partnerRoles.TECHNICAL_PARTNER_SUPER_USER}
        onClick={onRoleSelect}
        error={roleError}
        disabled={currentUserRole !== partnerRoles.ADMIN}
      >
        <RoleIcon icon={RoleImages.TechnicalAdmin} />
        {t('tech_partner_admin')}
      </Button>
      <Button
        variant="create-account-buttons"
        selected={role === partnerRoles.TECHNICAL_PARTNER_USER}
        value={partnerRoles.TECHNICAL_PARTNER_USER}
        onClick={onRoleSelect}
        error={roleError}
        disabled={
          currentUserRole !== partnerRoles.ADMIN &&
          currentUserRole !== partnerRoles.TECHNICAL_PARTNER_SUPER_USER
        }
      >
        <RoleIcon icon={RoleImages.TechnicalUser} />
        {t('tech_partner')}
      </Button> */}
    </>
  );

  return (
    <CreateContainer>
      <TitleContainer>
        <MainFont type="createAccountHeaders">{t('create_new')}</MainFont>
      </TitleContainer>
      <ItemContainer>
        <InputContainer
          error={error}
          value={email}
          onChange={onInputChange}
          placeholder={t('enter_email')}
        />
        {canShowDropdown() && (
          <DropDown
            key="create-account-dropdown"
            options={companies || []}
            placeholder="Company"
            onChange={selected => setCompanySelected(selected?.value || null)}
            maxMenuHeight={320}
            detachedOptions
            variant="editaccountdata"
          />
        )}
        {error && <MainFont type="errorLabel">{t('invalid_email')}</MainFont>}
      </ItemContainer>
      {canChooseRole() && (
        <>
          <ItemContainer>
            <MainFont type="showLabel">{t('choose_a_role')}</MainFont>
          </ItemContainer>

          <ButtonContainer>{loadCorrectRoles()}</ButtonContainer>
        </>
      )}
      {isLoadingCreation ? (
        <AcceptButtonContainer id="button-container">
          <Button variant="accept-button" noPadding>
            <SpinnerIcon icon={spinnerIconWhite} marginRight={11} />
            <MainFont type="addAccountLabels">{t('loading')}</MainFont>
          </Button>
        </AcceptButtonContainer>
      ) : (
        <AcceptButtonContainer>
          <Button
            variant="accept-button"
            onClick={onCreateAccount}
            disabled={isDisabled()}
          >
            <MainFont type="addAccountLabels">{t('create')}</MainFont>
          </Button>
        </AcceptButtonContainer>
      )}
    </CreateContainer>
  );
};

CreateAccount.propTypes = {
  createAccount: PropTypes.func,
  currentUserRole: PropTypes.string,
  createAccountCorporate: PropTypes.func,
  companies: PropTypes.array,
  currentCompanyCode: PropTypes.string,
  isLoadingCreation: PropTypes.bool
};

export default connect(
  state => ({
    currentUserRole: state?.authentication?.role,
    companies: state?.people?.companies,
    currentCompanyCode: state?.authentication?.accountInfo?.companyCode,
    isLoadingCreation: state?.people?.isLoadingCreation
  }),
  dispatch => ({
    createAccount: payload =>
      dispatch({ type: CREATE_ACCOUNT._REQUEST, payload })
  })
)(CreateAccount);
